import { useCallback, useMemo, useState, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UpdatedFact } from '@/components/product-insights/fab-ui';
import {
  PiDeniedPage,
  PiEmpty,
  PiError,
  PiFilterChips,
  PiPageLayout,
  PiTabs,
  PiToolbar,
} from '@/components/product-insights/pi-kit';
import { useFabFilters } from '@/components/product-insights/use-fab-data';
import {
  ChallengeEditionCard,
  ChallengeEditionCardSkeleton,
} from '@/components/product-insights/challenges/ChallengeEditionCard';
import { ChallengeSchoolsTable } from '@/components/product-insights/challenges/ChallengeSchoolsTable';
import { ChallengesOverview } from '@/components/product-insights/challenges/ChallengesOverview';
import {
  COMPETITION_LABEL,
  type ChallengeCompetitionFilter,
} from '@/components/product-insights/challenges/challenges-model';
import { useChallengesSummary } from '@/components/product-insights/challenges/use-challenges-data';
import { Button, Select } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';

/**
 * /product-insights/challenges — Challenge Hub, owner 2026-10-18. The SEED
 * Hackathon and the SEED Business Challenge, run with partner schools: what
 * each edition delivered for them (participants, finalists, winners, where the
 * participants study, who later applied / was admitted / enrolled). Live read
 * through EMERGE's `/product-insights/challenges` proxy; nothing stored.
 * Built on the shared Product Insights template (`pi-kit`, 2026-10-19).
 *
 * URL state (every default is an absent key; nothing written on arrival):
 * | Key           | Meaning                                  | Default      |
 * |---------------|------------------------------------------|--------------|
 * | `cycle`       | SEED business cycle id (SEED FAB's list) | All editions |
 * | `competition` | `hackathon` / `business`                 | all          |
 * | `tab`         | `schools` = By client school             | Editions     |
 *
 * - Unlike LAE, the default is ALL EDITIONS (no cycle sent): there are only a
 *   handful of editions, and a cycle often holds none. The cycle list is SEED
 *   FAB's (1 Jun – 31 May), never EMERGE cycles.
 * - Without `product-insights` read nothing is fetched: the refusal renders
 *   before the data component mounts.
 * - `meta.dataAsOf` (the static import date) is a fact about the data, shown
 *   in the facts line as "Data as of …".
 */

const CYCLE_PARAM = 'cycle';
const COMPETITION_PARAM = 'competition';
const TAB_PARAM = 'tab';

const COMPETITION_URL: Record<Exclude<ChallengeCompetitionFilter, 'all'>, string> = {
  hackathon: 'hackathon',
  business_challenge: 'business',
};

const ALL_COMPETITIONS = 'all';
const ALL_EDITIONS = '';

type ChallengesTab = 'editions' | 'schools';
const SCHOOLS_TAB_URL = 'schools';

function parseCompetition(raw: string | null): ChallengeCompetitionFilter {
  if (raw === COMPETITION_URL.hackathon) return 'hackathon';
  if (raw === COMPETITION_URL.business_challenge) return 'business_challenge';
  return 'all';
}

function parseCycle(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function parseTab(raw: string | null): ChallengesTab {
  return raw === SCHOOLS_TAB_URL ? 'schools' : 'editions';
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return fallback;
}

export function ProductInsightsChallengesPage() {
  const { ability } = useAuth();
  if (!ability.canRead('product-insights')) {
    return <PiDeniedPage product="challenges" title="Challenge Hub" />;
  }
  return <ChallengesContent />;
}

function ChallengesContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cycleId = parseCycle(searchParams.get(CYCLE_PARAM));
  const competition = parseCompetition(searchParams.get(COMPETITION_PARAM));
  const tab = parseTab(searchParams.get(TAB_PARAM));

  /* --- data ------------------------------------------------------------- */

  // The cycle list only; `null` asks SEED FAB for its current catalogue.
  const filtersResult = useFabFilters(null);
  const { filters } = filtersResult;
  const reloadFilters = filtersResult.reload;

  const [reloadToken, setReloadToken] = useState(0);
  const summary = useChallengesSummary(cycleId, competition, reloadToken);

  const refresh = useCallback(() => setReloadToken((n) => n + 1), []);
  const retry = useCallback(() => {
    reloadFilters();
    setReloadToken((n) => n + 1);
  }, [reloadFilters]);

  const selectedCycle = useMemo(
    () => (cycleId === null ? null : (filters?.cycles.find((c) => c.id === cycleId) ?? null)),
    [filters, cycleId],
  );

  /* --- URL writers ------------------------------------------------------ */

  const writeParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value === null) params.delete(key);
          else params.set(key, value);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onCycleChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const raw = event.target.value;
      writeParam(CYCLE_PARAM, raw === ALL_EDITIONS ? null : raw);
    },
    [writeParam],
  );

  const onCompetitionChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const next = event.target.value;
      writeParam(
        COMPETITION_PARAM,
        next === 'hackathon' || next === 'business_challenge' ? COMPETITION_URL[next] : null,
      );
    },
    [writeParam],
  );

  const onTabChange = useCallback(
    (next: ChallengesTab) => writeParam(TAB_PARAM, next === 'schools' ? SCHOOLS_TAB_URL : null),
    [writeParam],
  );

  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete(CYCLE_PARAM);
        params.delete(COMPETITION_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  /* --- render ----------------------------------------------------------- */

  const data = summary.data;
  const dataAsOf = data?.dataAsOf ?? null;
  // A cycle-list failure only empties the picker; the figures still stand.
  const pageError = summary.error ?? (filters === null ? filtersResult.error : null);
  const showData = data !== null || pageError === null;
  // A linked cycle not (yet) in the list still needs an option so the select shows it.
  const cycleUnlisted = cycleId !== null && selectedCycle === null;
  const cycleLabel = selectedCycle?.name ?? (cycleId === null ? null : `Cycle ${cycleId}`);
  const filtered = cycleId !== null || competition !== 'all';

  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (cycleLabel !== null) {
    chips.push({ key: 'cycle', label: cycleLabel, onRemove: () => writeParam(CYCLE_PARAM, null) });
  }
  if (competition !== 'all') {
    chips.push({
      key: 'competition',
      label: COMPETITION_LABEL[competition],
      onRemove: () => writeParam(COMPETITION_PARAM, null),
    });
  }

  const factParts: string[] = [];
  if (selectedCycle !== null) {
    factParts.push(
      `${formatDateOnly(selectedCycle.startDate)} – ${formatDateOnly(selectedCycle.endDate)}`,
    );
  }
  if (dataAsOf !== null) factParts.push(`Data as of ${formatDateOnly(dataAsOf)}`);

  const tabs = [
    { key: 'editions', label: 'Editions', count: data?.editions.length ?? null },
    { key: 'schools', label: 'By client school', count: data?.clientSchools.length ?? null },
  ] as const;

  const clearAction = filtered ? (
    <Button variant="secondary" size="sm" onClick={resetFilters}>
      Clear filters
    </Button>
  ) : undefined;

  return (
    <PiPageLayout
      product="challenges"
      title="Challenge Hub"
      facts={factParts.length > 0 ? factParts.join(' · ') : undefined}
      actions={
        <UpdatedFact
          fetchedAt={data?.fetchedAt ?? null}
          refreshing={summary.refreshing}
          onRefresh={refresh}
        />
      }
    >
      <PiToolbar
        chips={
          chips.length > 0 ? <PiFilterChips chips={chips} onReset={resetFilters} /> : undefined
        }
      >
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Cycle"
            value={cycleId !== null ? String(cycleId) : ALL_EDITIONS}
            onChange={onCycleChange}
          >
            <option value={ALL_EDITIONS}>All editions</option>
            {cycleUnlisted && <option value={String(cycleId)}>{`Cycle ${cycleId}`}</option>}
            {filters?.cycles.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.isCurrent ? `${c.name} (current)` : c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-48 max-sm:w-full">
          <Select
            aria-label="Competition"
            value={competition === 'all' ? ALL_COMPETITIONS : competition}
            onChange={onCompetitionChange}
          >
            <option value={ALL_COMPETITIONS}>All competitions</option>
            <option value="hackathon">{COMPETITION_LABEL.hackathon}</option>
            <option value="business_challenge">{COMPETITION_LABEL.business_challenge}</option>
          </Select>
        </div>
      </PiToolbar>

      {pageError !== null && pageError !== undefined && (
        <PiError
          message={errorMessage(pageError, 'Challenge Hub could not be reached.')}
          onRetry={retry}
        />
      )}

      {showData && (
        <>
          <ChallengesOverview data={data} loading={summary.loading} />

          <PiTabs tabs={tabs} value={tab} onChange={onTabChange} ariaLabel="Challenge Hub views" />

          <div
            role="tabpanel"
            aria-label={tab === 'editions' ? 'Editions' : 'By client school'}
            className="grid min-w-0 gap-4"
          >
            {tab === 'editions' ? (
              data === null ? (
                <ChallengeEditionCardSkeleton />
              ) : data.editions.length === 0 ? (
                <PiEmpty
                  title={cycleId === null ? 'No editions yet.' : 'No editions in this cycle.'}
                  action={clearAction}
                />
              ) : (
                <div
                  className={
                    summary.refreshing
                      ? 'grid min-w-0 gap-4 opacity-70 xl:grid-cols-2'
                      : 'grid min-w-0 gap-4 xl:grid-cols-2'
                  }
                  aria-busy={summary.refreshing || undefined}
                >
                  {data.editions.map((edition) => (
                    <ChallengeEditionCard key={edition.key} edition={edition} />
                  ))}
                </div>
              )
            ) : (
              <ChallengeSchoolsTable data={data} loading={summary.refreshing} />
            )}
          </div>
        </>
      )}
    </PiPageLayout>
  );
}

export default ProductInsightsChallengesPage;
